import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "./styles/chart3d.css";
import "./styles/accessibility.css";
import "./styles/responsive.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PermissionsProvider } from "./context/PermissionsContext";
import { BrandingProvider } from "./context/BrandingContext";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/auth/LoginPage";
import SignupPage from "./pages/auth/SignupPage";
import RoleSelectPage from "./pages/auth/RoleSelectPage";
import WorkspaceSelectPage from "./pages/auth/WorkspaceSelectPage";
import OnboardingWizardPage from "./pages/auth/OnboardingWizardPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import AuthCallback from "./pages/auth/AuthCallback";

// Dashboards
import SuperAdminDashboard from "./pages/dashboard/SuperAdminDashboard";
import TenantAdminDashboard from "./pages/dashboard/TenantAdminDashboard";

// Admin Pages
import TenantsPage from "./pages/admin/TenantsPage";
import UsersPage from "./pages/admin/UsersPage";
import CoursesPage from "./pages/admin/CoursesPage";
import SessionsPage from "./pages/admin/SessionsPage";
import CalendarPage from "./pages/admin/CalendarPage";
import SessionDetailPage from "./pages/admin/SessionDetailPage";
import LearningPathsPage from "./pages/admin/LearningPathsPage";
import LearningPathDetailPage from "./pages/admin/LearningPathDetailPage";
import CompliancePage from "./pages/admin/CompliancePage";
import ComplianceDetailPage from "./pages/admin/ComplianceDetailPage";
import AssessmentsPage from "./pages/admin/AssessmentsPage";
import AssessmentDetailPage from "./pages/admin/AssessmentDetailPage";
import TeamPage from "./pages/admin/TeamPage";
import GamificationPage from "./pages/admin/GamificationPage";
import SettingsPage from "./pages/admin/SettingsPage";
import SettingsHub from "./pages/admin/SettingsHub";
import BillingPage from "./pages/admin/BillingPage";
import SupportPage from "./pages/admin/SupportPage";
import EmployeeProfilePage from "./pages/admin/EmployeeProfilePage";
import AnalyticsPage from "./pages/admin/AnalyticsPage";
import TeamAnalyticsPage from "./pages/admin/TeamAnalyticsPage";
import ExecutiveAnalyticsPage from "./pages/admin/ExecutiveAnalyticsPage";
import AuditLogPage from "./pages/admin/AuditLogPage";
import RiskAssessmentPage from "./pages/admin/RiskAssessmentPage";
import CertificatesPage from "./pages/admin/CertificatesPage";
import AnnouncementsPage from "./pages/admin/AnnouncementsPage";
import ReportsPage from "./pages/admin/ReportsPage";

// Course Pages
import CourseDetailPage from "./pages/course/CourseDetailPage";
import AdminCourseDetailPage from "./pages/admin/CourseDetailPage";
import TemplatesPage from "./pages/admin/TemplatesPage";
import AdminOpsPage from "./pages/admin/AdminOpsPage";
import TenantDetailPage from "./pages/admin/TenantDetailPage";
import ContentLibraryPage from "./pages/admin/ContentLibraryPage";
import AdvancedToolsPage from "./pages/admin/AdvancedToolsPage";
import AdminSettingsPage from "./pages/admin/AdminSettingsPage";
import AllUsersPage from "./pages/admin/AllUsersPage";
import StaffProfilePage from "./pages/admin/StaffProfilePage";
import CoursePlayerPage from "./pages/course/CoursePlayerPage";
import InteractiveCoursePlayer from "./pages/course/InteractiveCoursePlayer";

// Catalog
import CatalogPage from "./pages/catalog/CatalogPage";

// Learner
import LearnerDashboardPage from "./pages/learner/LearnerDashboardPage";
import LearnerAssessmentsPage from "./pages/learner/LearnerAssessmentsPage";
import AssessmentTakePage from "./pages/learner/AssessmentTakePage";
import MyProgressPage from "./pages/learner/MyProgressPage";
import MyLearningPage from "./pages/learner/MyLearningPage";
import LearnerAnnouncementsPage from "./pages/learner/LearnerAnnouncementsPage";
import LearnerCertificatesPage from "./pages/learner/LearnerCertificatesPage";
import LearnerSessionsPage from "./pages/learner/LearnerSessionsPage";
import LearnerGamificationPage from "./pages/learner/LearnerGamificationPage";
import LearnerLearningPathsPage from "./pages/learner/LearnerLearningPathsPage";
import LearnerCompliancePage from "./pages/learner/LearnerCompliancePage";
import LearnerCatalogPage from "./pages/learner/LearnerCatalogPage";
import LearnerSettingsPage from "./pages/learner/LearnerSettingsPage";
import LearnerSupportPage from "./pages/learner/LearnerSupportPage";
import LearnerNotificationsPage from "./pages/learner/LearnerNotificationsPage";
import ProductUpdatesPage from "./pages/shared/ProductUpdatesPage";
import TrainingDocsPage from "./pages/shared/TrainingDocsPage";
import KeyboardShortcutsPage from "./pages/shared/KeyboardShortcutsPage";

// Manager
import ManagerTeamCompliancePage from "./pages/manager/ManagerTeamCompliancePage";
import ManagerTeamAnalyticsPage from "./pages/manager/ManagerTeamAnalyticsPage";
import ManagerTeamPage from "./pages/manager/ManagerTeamPage";
import ManagerTeamMemberPage from "./pages/manager/ManagerTeamMemberPage";
import ManagerTeamEnrollPage from "./pages/manager/ManagerTeamEnrollPage";
import AccessRequestsPage from "./pages/admin/AccessRequestsPage";

// New Admin Pages
import EmbedEnginePage from "./pages/admin/EmbedEnginePage";
import QuoteRequestsPage from "./pages/admin/QuoteRequestsPage";
import NotificationsPage from "./pages/admin/NotificationsPage";
import IntegrationsPage from "./pages/admin/IntegrationsPage";
import GroupsDepartmentsPage from "./pages/admin/GroupsDepartmentsPage";
import MigrationPage from "./pages/admin/MigrationPage";
import SSOConfigPage from "./pages/admin/SSOConfigPage";
import LTIManagementPage from "./pages/admin/LTIManagementPage";
import InvoicesPage from "./pages/admin/InvoicesPage";
import ScheduledReportsPage from "./pages/admin/ScheduledReportsPage";
import CertificateTemplatesPage from "./pages/admin/CertificateTemplatesPage";

// Shared
import Footer from "./components/shared/Footer";
import SharedInboxPage from "./pages/shared/SharedInboxPage";
import DiscussionForumPage from "./pages/shared/DiscussionForumPage";
import PrivacyPolicyPage from "./pages/shared/PrivacyPolicyPage";
import { CookieConsentBanner } from "./components/shared/CookieConsentBanner";

// Compliance Dashboard
import ComplianceDashboardPage from "./pages/admin/ComplianceDashboardPage";
import TestResultsCenterPage from "./pages/admin/TestResultsCenterPage";

// Feature Flags
import FeatureFlagsPage from "./pages/admin/FeatureFlagsPage";

// Error Logs
import ErrorLogsPage from "./pages/admin/ErrorLogsPage";

// Service Requests
import ServiceRequestsPage from "./pages/admin/ServiceRequestsPage";
import DemoDataPage from "./pages/admin/DemoDataPage";
import SystemHealthPage from "./pages/shared/SystemHealthPage";
import ActionCenterPage from "./pages/shared/ActionCenterPage";
import TenantRevenuePage from "./pages/admin/TenantRevenuePage";

// Beta Gate
import BetaGatePage from "./pages/BetaGatePage";

// Onboarding
import TenantOnboardingWizard from "./pages/auth/TenantOnboardingWizard";

import "./index.css";

import ManagerDashboard from "./pages/dashboard/ManagerDashboard";

// Protected Route Component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (roles.length > 0 && !roles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === "super_admin") {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user?.role === "tenant_admin") {
      return <Navigate to="/tenant/dashboard" replace />;
    } else if (user?.role === "manager") {
      return <Navigate to="/manager/dashboard" replace />;
    } else {
      return <Navigate to="/learner/dashboard" replace />;
    }
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect based on role
    if (user?.role === "super_admin") {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (user?.role === "tenant_admin") {
      return <Navigate to="/tenant/dashboard" replace />;
    } else if (user?.role === "manager") {
      return <Navigate to="/manager/dashboard" replace />;
    } else {
      return <Navigate to="/learner/dashboard" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  const showFooter = ["/auth/login", "/auth/signup", "/auth/forgot-password"].includes(location.pathname);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect OAuth callback session_id in URL fragment synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <a href="#main-content" className="skip-link" data-testid="skip-link">
        Skip to main content
      </a>
      <div id="main-content" role="main">
        <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      
      {/* Auth Routes */}
      <Route
        path="/auth/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/auth/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route path="/auth/role" element={<RoleSelectPage />} />
      <Route path="/auth/workspace" element={<WorkspaceSelectPage />} />
      <Route path="/auth/onboarding" element={<OnboardingWizardPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />

      {/* Super Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <TenantsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenants/:tenantId"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <TenantDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AllUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/platform-staff"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/staff/:userId"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <StaffProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <CoursesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/courses/:courseId"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminCourseDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <TemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/operations"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminOpsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/content-library"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <ContentLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/advanced-tools"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdvancedToolsPage />
          </ProtectedRoute>
        }
      />

      {/* New Admin Sections */}
      <Route path="/admin/embed-engine" element={<ProtectedRoute roles={["super_admin"]}><EmbedEnginePage /></ProtectedRoute>} />
      <Route path="/tenant/embed-engine" element={<ProtectedRoute roles={["tenant_admin"]}><EmbedEnginePage /></ProtectedRoute>} />
      <Route path="/admin/compliance-dashboard" element={<ProtectedRoute roles={["super_admin"]}><ComplianceDashboardPage /></ProtectedRoute>} />
      <Route path="/admin/test-results-center" element={<ProtectedRoute roles={["super_admin"]}><TestResultsCenterPage /></ProtectedRoute>} />
      <Route path="/admin/quote-requests" element={<ProtectedRoute roles={["super_admin"]}><QuoteRequestsPage /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute roles={["super_admin"]}><NotificationsPage /></ProtectedRoute>} />
      <Route path="/admin/integrations" element={<ProtectedRoute roles={["super_admin"]}><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/admin/groups" element={<ProtectedRoute roles={["super_admin"]}><GroupsDepartmentsPage /></ProtectedRoute>} />
      <Route path="/admin/migration" element={<ProtectedRoute roles={["super_admin"]}><MigrationPage /></ProtectedRoute>} />
      <Route path="/admin/sso" element={<ProtectedRoute roles={["super_admin", "tenant_admin"]}><SSOConfigPage /></ProtectedRoute>} />
      <Route path="/admin/lti" element={<ProtectedRoute roles={["super_admin"]}><LTIManagementPage /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute roles={["super_admin", "tenant_admin"]}><InvoicesPage /></ProtectedRoute>} />
      <Route path="/admin/scheduled-reports" element={<ProtectedRoute roles={["super_admin", "tenant_admin"]}><ScheduledReportsPage /></ProtectedRoute>} />
      <Route path="/admin/certificate-templates" element={<ProtectedRoute roles={["super_admin", "tenant_admin"]}><CertificateTemplatesPage /></ProtectedRoute>} />
      <Route path="/admin/feature-flags" element={<ProtectedRoute roles={["super_admin"]}><FeatureFlagsPage /></ProtectedRoute>} />
      <Route path="/admin/error-logs" element={<ProtectedRoute roles={["super_admin"]}><ErrorLogsPage /></ProtectedRoute>} />
      <Route path="/admin/service-requests" element={<ProtectedRoute roles={["super_admin"]}><ServiceRequestsPage /></ProtectedRoute>} />
              <Route path="/admin/demo-data" element={<ProtectedRoute roles={["super_admin"]}><DemoDataPage /></ProtectedRoute>} />

      {/* Shared Pages */}
      <Route path="/shared/inbox" element={<ProtectedRoute><SharedInboxPage /></ProtectedRoute>} />
      <Route path="/discussions/:courseId" element={<ProtectedRoute><DiscussionForumPage /></ProtectedRoute>} />

      {/* Tenant Onboarding Wizard */}
      <Route path="/onboarding/wizard" element={<ProtectedRoute roles={["tenant_admin"]}><TenantOnboardingWizard /></ProtectedRoute>} />

      {/* Tenant Admin Routes */}
      <Route
        path="/tenant/dashboard"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <TenantAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/courses"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <CatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/courses/:courseId"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <AdminCourseDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Access Requests - Admin, Tenant Admin, Manager */}
      <Route
        path="/admin/access-requests"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AccessRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/access-requests"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <AccessRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/access-requests"
        element={
          <ProtectedRoute roles={["manager"]}>
            <AccessRequestsPage />
          </ProtectedRoute>
        }
      />

      {/* Catalog Routes (accessible to all authenticated users) */}
      <Route
        path="/admin/catalog"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <CatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/catalogue"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <CatalogPage />
          </ProtectedRoute>
        }
      />

      {/* ILT Sessions & Calendar */}
      <Route
        path="/admin/sessions"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <SessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/sessions"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <SessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/calendar"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/calendar"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sessions/:sessionId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "learner"]}>
            <SessionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/sessions/:sessionId"
        element={
          <ProtectedRoute roles={["tenant_admin", "learner"]}>
            <SessionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/sessions/:sessionId"
        element={
          <ProtectedRoute roles={["learner"]}>
            <SessionDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/sessions/:sessionId"
        element={
          <ProtectedRoute roles={["manager"]}>
            <SessionDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Learning Paths */}
      <Route
        path="/admin/learning-paths"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <LearningPathsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/learning-paths/:pathId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <LearningPathDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/learning-paths"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <LearningPathsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/learning-paths/:pathId"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <LearningPathDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Compliance */}
      <Route
        path="/admin/compliances"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <CompliancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/compliances/:complianceId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <ComplianceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/compliances"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <CompliancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/compliances/:complianceId"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <ComplianceDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Learner Routes */}

      {/* Employee Profile */}
      <Route
        path="/admin/team/:userId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <EmployeeProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/team/:userId"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <EmployeeProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Team Analytics */}
      <Route
        path="/team/analytics"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "manager"]}>
            <TeamAnalyticsPage />
          </ProtectedRoute>
        }
      />

      {/* Manager Routes */}
      <Route
        path="/manager/dashboard"
        element={
          <ProtectedRoute roles={["manager"]}>
            <ManagerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/team"
        element={
          <ProtectedRoute roles={["manager"]}>
            <ManagerTeamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/team-enroll"
        element={
          <ProtectedRoute roles={["manager"]}>
            <ManagerTeamEnrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/team/:userId"
        element={
          <ProtectedRoute roles={["manager"]}>
            <ManagerTeamMemberPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/team-compliance"
        element={
          <ProtectedRoute roles={["manager"]}>
            <ManagerTeamCompliancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/team-analytics"
        element={
          <ProtectedRoute roles={["manager"]}>
            <ManagerTeamAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/announcements"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerAnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/learning-paths"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerLearningPathsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/learning-paths/:pathId"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearningPathDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/compliance"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerCompliancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/certificates"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerCertificatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/assessments"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerAssessmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/sessions"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerSessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/catalog"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/gamification"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerGamificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/settings"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/support"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerSupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/notifications"
        element={
          <ProtectedRoute roles={["manager"]}>
            <LearnerNotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/notifications"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <LearnerNotificationsPage />
          </ProtectedRoute>
        }
      />

      {/* Analytics */}
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/analytics"
        element={
          <ProtectedRoute roles={["tenant_admin", "manager"]}>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/executive-analytics"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <ExecutiveAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/executive-analytics"
        element={
          <ProtectedRoute roles={["tenant_admin", "manager"]}>
            <ExecutiveAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/audit-logs"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/risk-assessment"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <RiskAssessmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/risk-assessment"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <RiskAssessmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/reports"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/certificates"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "manager"]}>
            <CertificatesPage />
          </ProtectedRoute>
        }
      />

      {/* Announcements */}
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/announcements"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <AnnouncementsPage />
          </ProtectedRoute>
        }
      />


      {/* Interactive Course Player */}
      <Route
        path="/learn/:courseId"
        element={
          <ProtectedRoute roles={["learner", "manager", "tenant_admin", "super_admin"]}>
            <InteractiveCoursePlayer />
          </ProtectedRoute>
        }
      />

      <Route
        path="/learner/dashboard"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/assessments"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerAssessmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/assessments/:assessmentId"
        element={
          <ProtectedRoute roles={["learner", "tenant_admin", "super_admin"]}>
            <AssessmentTakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/announcements"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerAnnouncementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/certificates"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerCertificatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/sessions"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerSessionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/gamification"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerGamificationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/learning-paths"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerLearningPathsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/compliance"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerCompliancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/catalog"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerCatalogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/settings"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learner/support"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerSupportPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/learner/notifications"
        element={
          <ProtectedRoute roles={["learner"]}>
            <LearnerNotificationsPage />
          </ProtectedRoute>
        }
      />
      {/* Shared pages (accessible by all roles) */}
      <Route
        path="/product-updates"
        element={
          <ProtectedRoute>
            <ProductUpdatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training-docs"
        element={
          <ProtectedRoute>
            <TrainingDocsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/keyboard-shortcuts"
        element={
          <ProtectedRoute>
            <KeyboardShortcutsPage />
          </ProtectedRoute>
        }
      />
      {/* Assessments */}
      <Route
        path="/admin/assessments"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <AssessmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assessments/:assessmentId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <AssessmentDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/assessments"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <AssessmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/assessments/:assessmentId"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <AssessmentDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Team Management */}
      <Route
        path="/admin/team"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <TeamPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/team"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <TeamPage />
          </ProtectedRoute>
        }
      />

      {/* Gamification */}
      <Route
        path="/admin/gamification"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "learner"]}>
            <GamificationPage />
          </ProtectedRoute>
        }
      />

      {/* Revenue & Accounts — Super Admin */}
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tenant-revenue/:tenantId"
        element={
          <ProtectedRoute roles={["super_admin"]}>
            <TenantRevenuePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings/:section"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/settings"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <SettingsHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/settings/:section"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Billing */}
      <Route
        path="/admin/billing"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/billing"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <BillingPage />
          </ProtectedRoute>
        }
      />

      {/* Support */}
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin"]}>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant/support"
        element={
          <ProtectedRoute roles={["tenant_admin"]}>
            <SupportPage />
          </ProtectedRoute>
        }
      />

      {/* Course Detail & Player Routes */}
      <Route
        path="/course/:courseId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "learner"]}>
            <CourseDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/course/:courseId/player"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "learner"]}>
            <CoursePlayerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/course/:courseId/player/:enrollmentId"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "learner"]}>
            <CoursePlayerPage />
          </ProtectedRoute>
        }
      />

      {/* My Progress & My Courses (all authenticated roles) */}
      <Route
        path="/my-progress"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "manager", "learner"]}>
            <MyProgressPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-courses"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "manager", "learner"]}>
            <MyLearningPage />
          </ProtectedRoute>
        }
      />

      {/* Action Center (all roles) */}
      <Route
        path="/action-center"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "manager", "learner"]}>
            <ActionCenterPage />
          </ProtectedRoute>
        }
      />

      {/* System Health (all roles) */}
      <Route
        path="/system-health"
        element={
          <ProtectedRoute roles={["super_admin", "tenant_admin", "manager", "learner"]}>
            <SystemHealthPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </div>
    {showFooter && <Footer />}
    {/* Live region for dynamic announcements */}
    <div aria-live="polite" aria-atomic="true" className="sr-only" id="a11y-announcer" data-testid="a11y-announcer"></div>
    </>
  );
}

import ErrorBoundary from "./components/shared/ErrorBoundary";

function App() {
  const [betaUnlocked, setBetaUnlocked] = useState(
    () => localStorage.getItem("odesie_beta_access") === "granted"
  );

  if (!betaUnlocked) {
    return <BetaGatePage onUnlock={() => setBetaUnlocked(true)} />;
  }

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <PermissionsProvider>
            <BrandingProvider>
              <Toaster position="top-right" richColors closeButton />
              <CookieConsentBanner />
              <AppRoutes />
            </BrandingProvider>
          </PermissionsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
